import { Box, Center, Loader, Tabs } from '@mantine/core'
import {
  IconActivity,
  IconDeviceDesktop,
  IconListDetails,
  IconTerminal2,
  IconVideo
} from '@tabler/icons-react'
import { ReactNode } from 'react'
import { FormattedMessage } from 'react-intl'
import { ErrorWithReload } from '@/components/ui-shared/ErrorWithReload/ErrorWithReload'
import { ListItemDrawer } from '@/components/ui-shared/ListItemDrawer/ListItemDrawer'
import { useGetDeviceGroups } from '@/queries/deploymentQueries'
import { useGetDeviceDetails } from '@/queries/deviceQueries'
import { DeploymentGroup } from '@/types/deploymentGroup'
import { Device } from '@/types/device'
import { getDevicesCameras } from '@/utils/device/getDeviceAttr'
import { DeploymentAppLogsTab } from './DeploymentAppLogsTab'
import { DeploymentDeviceTab } from './DeploymentDeviceTab'
import { DeploymentGroupCamerasTab } from './DeploymentGroupCamerasTab'
import { DeploymentGroupOverviewTab } from './DeploymentGroupOverviewTab'
import { DeploymentObservabilityTab } from './DeploymentObservabilityTab'

export type DeploymentGroupDrawerTab =
  'overview' | 'cameras' | 'device' | 'observability' | 'app-logs'

type DeploymentGroupDrawerProps = {
  opened: boolean
  group: DeploymentGroup
  label: number
  deviceId: string
  appId: string
  appName: string
  initialTab?: DeploymentGroupDrawerTab
  initialDeploymentId?: string
  onClose: () => void
}

export const DeploymentGroupDrawer = ({
  opened,
  group,
  label,
  deviceId,
  appId,
  appName,
  initialTab = 'overview',
  initialDeploymentId,
  onClose
}: DeploymentGroupDrawerProps) => {
  const {
    data: device,
    isLoading,
    isError,
    refetch
  } = useGetDeviceDetails(deviceId, opened)
  const { data: deviceGroups } = useGetDeviceGroups(deviceId, opened)

  const renderWithDevice = (renderTab: (loadedDevice: Device) => ReactNode) => {
    if (isLoading) {
      return (
        <Center mih={240}>
          <Loader size="lg" />
        </Center>
      )
    }

    if (isError || !device) {
      return (
        <ErrorWithReload onReload={() => void refetch()}>
          <FormattedMessage id="devices.data.error" />
        </ErrorWithReload>
      )
    }

    return renderTab(device)
  }

  return (
    <ListItemDrawer
      opened={opened}
      title={
        <FormattedMessage
          id="deployments.groups.details.title"
          values={{
            app: appName,
            number: label,
            device: device?.label ?? deviceId
          }}
        />
      }
      size="60%"
      onClose={onClose}
    >
      <Box maw={1100}>
        <Tabs
          key={group.id}
          variant="outline"
          defaultValue={initialTab}
          keepMounted={false}
        >
          <Tabs.List>
            <Tabs.Tab
              value="overview"
              leftSection={<IconListDetails size={16} />}
            >
              <FormattedMessage id="deployments.groups.details.tabs.overview" />
            </Tabs.Tab>

            <Tabs.Tab value="cameras" leftSection={<IconVideo size={16} />}>
              <FormattedMessage id="deployments.groups.details.tabs.cameras" />
            </Tabs.Tab>

            <Tabs.Tab
              value="device"
              leftSection={<IconDeviceDesktop size={16} />}
            >
              <FormattedMessage id="deployments.details.tabs.device" />
            </Tabs.Tab>

            <Tabs.Tab
              value="observability"
              leftSection={<IconActivity size={16} />}
            >
              <FormattedMessage id="deployments.details.tabs.observability" />
            </Tabs.Tab>

            <Tabs.Tab
              value="app-logs"
              leftSection={<IconTerminal2 size={16} />}
            >
              <FormattedMessage id="deployments.details.tabs.appLogs" />
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="overview" pt="md">
            <DeploymentGroupOverviewTab group={group} device={device} />
          </Tabs.Panel>

          <Tabs.Panel value="cameras" pt="md">
            {renderWithDevice((loadedDevice) => (
              <DeploymentGroupCamerasTab
                group={group}
                device={loadedDevice}
                appId={appId}
                initialDeploymentId={initialDeploymentId}
                onRefresh={() => void refetch()}
              />
            ))}
          </Tabs.Panel>

          <Tabs.Panel value="device" pt="md">
            {renderWithDevice((loadedDevice) => (
              <DeploymentDeviceTab
                device={loadedDevice}
                onRefresh={() => void refetch()}
              />
            ))}
          </Tabs.Panel>

          <Tabs.Panel value="observability" pt="md">
            {renderWithDevice((loadedDevice) => (
              <DeploymentObservabilityTab
                device={loadedDevice}
                activeGpuIndexes={group.gpu ? [group.gpu.index] : []}
                appName={appName}
                groups={deviceGroups}
              />
            ))}
          </Tabs.Panel>

          <Tabs.Panel value="app-logs" pt="md">
            <DeploymentAppLogsTab
              scope={{
                kind: 'group',
                groupId: group.id,
                members: group.members
              }}
              cameras={device ? getDevicesCameras(device) : undefined}
              initialCamera={
                initialTab === 'app-logs' ? initialDeploymentId : undefined
              }
            />
          </Tabs.Panel>
        </Tabs>
      </Box>
    </ListItemDrawer>
  )
}
